export interface ScopeUnit {
  id: string;
  labelKey: string;
  schemaFields: string[];
  primaryField: string;
}

const WHOLE_UNIT_ID = "whole";

const quizz1: ScopeUnit[] = [
  {
    id: "question",
    labelKey: "question",
    schemaFields: ["question_text"],
    primaryField: "question_text",
  },
  {
    id: "answer-1",
    labelKey: "answer1",
    schemaFields: ["answer_1_text", "is_answer_1_true"],
    primaryField: "answer_1_text",
  },
  {
    id: "answer-2",
    labelKey: "answer2",
    schemaFields: ["answer_2_text", "is_answer_2_true"],
    primaryField: "answer_2_text",
  },
  {
    id: "answer-3",
    labelKey: "answer3",
    schemaFields: ["answer_3_text", "is_answer_3_true"],
    primaryField: "answer_3_text",
  },
  {
    id: "explanation-true",
    labelKey: "explanationTrue",
    schemaFields: ["explanation_true_text"],
    primaryField: "explanation_true_text",
  },
  {
    id: "explanation-false",
    labelKey: "explanationFalse",
    schemaFields: ["explanation_false_text"],
    primaryField: "explanation_false_text",
  },
];

// content-quizz-2: answers are images (`answer_X_image`) with a correctness
// boolean (`is_answer_X_true`). Neither is meaningful for text AI gen, so
// scope only exposes the question and the two explanations.
const quizz2: ScopeUnit[] = [
  {
    id: "question",
    labelKey: "question",
    schemaFields: ["question_text"],
    primaryField: "question_text",
  },
  {
    id: "explanation-true",
    labelKey: "explanationTrue",
    schemaFields: ["explanation_true_text"],
    primaryField: "explanation_true_text",
  },
  {
    id: "explanation-false",
    labelKey: "explanationFalse",
    schemaFields: ["explanation_false_text"],
    primaryField: "explanation_false_text",
  },
];

// content-quizz-3: list_answers / correct_answers are JSON arrays, only
// editable from Spatial Studio. We never expose those items as AI-gen
// scope units (the user couldn't verify the result in this form).
const quizz3: ScopeUnit[] = [
  {
    id: "question",
    labelKey: "question",
    schemaFields: ["question_text"],
    primaryField: "question_text",
  },
  {
    id: "explanation-true",
    labelKey: "explanationTrue",
    schemaFields: ["explanation_true_text"],
    primaryField: "explanation_true_text",
  },
  {
    id: "explanation-false",
    labelKey: "explanationFalse",
    schemaFields: ["explanation_false_text"],
    primaryField: "explanation_false_text",
  },
];

// content-quizz-sort: the `answers` JSON array is only editable from
// Spatial Studio, so we never expose those items as AI-gen scope units.
const quizzSort: ScopeUnit[] = [
  {
    id: "question",
    labelKey: "question",
    schemaFields: ["question"],
    primaryField: "question",
  },
  {
    id: "explanation-true",
    labelKey: "explanationTrue",
    schemaFields: ["explanation_true_text"],
    primaryField: "explanation_true_text",
  },
  {
    id: "explanation-false",
    labelKey: "explanationFalse",
    schemaFields: ["explanation_false_text"],
    primaryField: "explanation_false_text",
  },
];

// content-quizz-image-multiple: `list_answers` is a JSON array, edited
// only from Spatial Studio. Same reasoning as quizz-3 / quizz-sort.
const quizzImageMultiple: ScopeUnit[] = [
  {
    id: "question",
    labelKey: "question",
    schemaFields: ["question_text"],
    primaryField: "question_text",
  },
  {
    id: "explanation-true",
    labelKey: "explanationTrue",
    schemaFields: ["explanation_true_text"],
    primaryField: "explanation_true_text",
  },
  {
    id: "explanation-false",
    labelKey: "explanationFalse",
    schemaFields: ["explanation_false_text"],
    primaryField: "explanation_false_text",
  },
];

const yesNo: ScopeUnit[] = [
  {
    id: "question",
    labelKey: "question",
    schemaFields: ["question_text", "is_yes_good_answer"],
    primaryField: "question_text",
  },
  {
    id: "yes-button",
    labelKey: "yesButton",
    schemaFields: ["yes_button_text"],
    primaryField: "yes_button_text",
  },
  {
    id: "no-button",
    labelKey: "noButton",
    schemaFields: ["no_button_text"],
    primaryField: "no_button_text",
  },
  {
    id: "explanation-true",
    labelKey: "explanationTrue",
    schemaFields: ["explanation_true_text"],
    primaryField: "explanation_true_text",
  },
  {
    id: "explanation-false",
    labelKey: "explanationFalse",
    schemaFields: ["explanation_false_text"],
    primaryField: "explanation_false_text",
  },
];

// content-speech-to-check: `keywords_info` is a JSON field edited from
// Spatial Studio only, so it's not exposed as an AI-gen scope unit.
const speechToCheck: ScopeUnit[] = [
  {
    id: "title",
    labelKey: "title",
    schemaFields: ["title"],
    primaryField: "title",
  },
  {
    id: "intro",
    labelKey: "intro",
    schemaFields: ["intro_text"],
    primaryField: "intro_text",
  },
  {
    id: "end",
    labelKey: "endText",
    schemaFields: ["end_text"],
    primaryField: "end_text",
  },
  {
    id: "fail",
    labelKey: "failText",
    schemaFields: ["fail_text"],
    primaryField: "fail_text",
  },
];

const zoneDetect: ScopeUnit[] = [
  {
    id: "title",
    labelKey: "title",
    schemaFields: ["title"],
    primaryField: "title",
  },
  {
    id: "entitled",
    labelKey: "entitled",
    schemaFields: ["entitled"],
    primaryField: "entitled",
  },
  {
    id: "instruction",
    labelKey: "instruction",
    schemaFields: ["instruction"],
    primaryField: "instruction",
  },
];

const elementCatch: ScopeUnit[] = [
  {
    id: "instructions",
    labelKey: "instructions",
    schemaFields: ["text_instructions"],
    primaryField: "text_instructions",
  },
  {
    id: "result",
    labelKey: "result",
    schemaFields: ["text_result"],
    primaryField: "text_result",
  },
];

// content-random-quizz schema only carries `end_text` as a webapp-editable
// text field. The nested `questions` JSON array is Spatial-Studio-only, and
// the prior list of `answer_X_text` / `is_answer_X_true` directives didn't
// match this model's attributes at all (they were copy-pasted from quizz-1).
const RANDOM_QUIZZ_FIELDS = ["end_text"];

const randomQuizz: ScopeUnit[] = [
  {
    id: WHOLE_UNIT_ID,
    labelKey: "whole",
    schemaFields: RANDOM_QUIZZ_FIELDS,
    primaryField: "end_text",
  },
];

const SCOPE_UNITS_BY_MODEL: Record<string, ScopeUnit[]> = {
  "content-quizz-1": quizz1,
  "content-quizz-2": quizz2,
  "content-quizz-3": quizz3,
  "content-quizz-sort": quizzSort,
  "content-quizz-image-multiple": quizzImageMultiple,
  "content-yes-no-question": yesNo,
  "content-random-quizz": randomQuizz,
  "content-speech-to-check": speechToCheck,
  "content-zone-detect": zoneDetect,
  "content-element-catch": elementCatch,
  // content-text and content-onboarding: single-field, no scope picker.
  "content-text": [],
  "content-onboarding": [],
};

export const getScopeUnits = (modelName: string): ScopeUnit[] =>
  SCOPE_UNITS_BY_MODEL[modelName] ?? [];

export const findScopeUnitForField = (
  modelName: string,
  fieldName: string,
): ScopeUnit | undefined => {
  const units = getScopeUnits(modelName);
  return units.find((u) => u.schemaFields.includes(fieldName));
};
